import {
  InfiniteData,
  QueryClient,
  QueryFilters,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  keepPreviousData as placeholderData,
} from '@tanstack/react-query';
import { APIQueryInput } from 'shared/api/APIQuery';
import { APISchema, castAPIData } from 'shared/api/APISchema';
import { APIPageResponse } from 'shared/api/CarrierAPIClient';

export type BaseAPIListQueryOptions<TData> = Omit<
  UseInfiniteQueryOptions<APIPageResponse<TData>>,
  | 'queryFn'
  | 'queryKey'
  | 'queryHash'
  | 'queryKeyHashFn'
  | 'isDataEqual'
  | 'getNextPageParam'
  | 'getPreviousPageParam'
  | 'initialPageParam'
>;

export interface APIListQueryOptions<
  TData,
> extends BaseAPIListQueryOptions<TData> {
  schema?: APISchema<TData>;
  normalize?: (response: unknown) => TData;
  initialPageParam?: number;
  keepPreviousData?: boolean;
}

export type APIListQueryData<TData = unknown> = InfiniteData<
  APIPageResponse<TData>
>;

export type APIListQueryResult<TData> = Omit<
  UseInfiniteQueryResult<APIPageResponse<TData>>,
  'data'
> & { data: InfiniteData<APIPageResponse<TData>> | undefined };

export function useAPIListQuery<TData>(
  input: APIQueryInput,
  query: (page: number) => Promise<APIPageResponse<TData>>,
  {
    schema,
    normalize,
    refetchOnWindowFocus = false,
    keepPreviousData = true,
    initialPageParam = 1,
    ...options
  }: APIListQueryOptions<TData>,
): APIListQueryResult<TData> {
  return useInfiniteQuery({
    initialPageParam,
    placeholderData: keepPreviousData ? placeholderData : undefined,
    ...options,
    queryKey: input,
    queryFn: ({ pageParam }) =>
      query(pageParam as number).then((response) => {
        if (schema || normalize) {
          response.data = response.data.map((entry) => {
            if (schema) {
              entry = castAPIData(schema, entry);
            }

            if (normalize) {
              entry = normalize(entry);
            }

            return entry;
          });
        }

        return response;
      }),
    refetchOnWindowFocus,
    getNextPageParam: (lastPage, allPages) => {
      const { next } = lastPage.pagination;

      if (next) {
        try {
          return new URL(next).searchParams.get('page');
        } catch {}
      }

      //loadboard return empty string for next page param
      //check if next is empty string and that all items are not loaded yet
      if (next === '') {
        const loadedCount = allPages.flatMap((page) => page.data).length;
        if (loadedCount < lastPage.pagination.count) {
          // number of loaded pages are represent the next page number because loadboard pagination starts from 0
          return allPages.length;
        }
      }

      return null;
    },
  }) as APIListQueryResult<TData>;
}

function forEachAPIListQueryItem<TData>(
  queryKey: APIQueryInput,
  queryClient: QueryClient,
  fn: (item: TData) => void | false,
  filters?: QueryFilters,
): void {
  for (const { state } of queryClient
    .getQueryCache()
    .findAll({ queryKey, ...filters })) {
    if (state.data) {
      const { pages } = state.data as APIListQueryData<TData>;

      for (const page of pages) {
        for (const item of page.data) {
          if (fn(item) === false) return;
        }
      }
    }
  }
}

export function findAPIListQueryItem<TData>(
  input: APIQueryInput,
  queryClient: QueryClient,
  fn: (item: TData) => boolean,
  filters?: QueryFilters,
): undefined | TData {
  let found: undefined | TData = undefined;

  forEachAPIListQueryItem<TData>(
    input,
    queryClient,
    (item) => {
      if (!fn(item)) return;
      found = item;
      return false;
    },
    filters,
  );

  return found;
}
